/* eslint-disable prettier/prettier */
import { Box, Button, CircularProgress, Grid } from '@mui/material'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import Container from '../../components/Container'
import TemplateRenderer from '../../components/TemplateRenderer'
import ErrorBanner from '../../components/shared/ErrorBanner'
import InlineLoading from '../../components/shared/InlineLoading'
import { putCommType } from '../../features/templateSlice'
import TemplateService from '../../services/templateService'
import { errorMessage } from '../../services/axios/errorMessage'
import { RADIUS, SURFACE } from '../../theme/tokens'

// Colour comes from the theme's containedSuccess / containedError overrides
// (Deep Blue on green, white on the darkened red; both AA, see
// theme/contrast.test.js). An sx `background` here lost to the theme's
// two-class containedPrimary rule, so both buttons rendered orange.
const decisionButtonSx = {
    m: 5,
    textTransform: 'none',
    fontWeight: 700,
    boxShadow: 'none',
    px: 4,
}

const ApproveTemplate = () => {
    const dispatch = useDispatch()
    const { template_id, comm_type } = useParams()
    const navigate = useNavigate()
    // The decision being sent ('approved' | 'disapproved'), so the spinner
    // shows on the button that was pressed; null when idle.
    const [submitting, setSubmitting] = useState(null)
    const [error, setError] = useState('')
    // loading | ready | missing (with the reason). A template that does not
    // load gets no Approve / Disapprove: they used to stay enabled, and
    // Approve answered with a raw server error.
    const [load, setLoad] = useState({ state: 'loading', reason: '' })

    useEffect(() => {
        if (!template_id) return
        // The channel from the link, so a body the preview answers as a plain
        // string (stored inline, with no comm_type) still renders as its
        // channel: an inline email showed as chat text.
        dispatch(putCommType(String(comm_type ?? '').toLowerCase()))
        dispatch(TemplateService.getTemplateData(template_id)).then((resp) => {
            const body = resp.payload?.data?.response
            if (resp.error || !body) {
                setLoad({
                    state: 'missing',
                    reason: resp.error
                        ? errorMessage({ data: resp.payload })
                        : 'The template has no content.',
                })
                return
            }
            setLoad({ state: 'ready', reason: '' })
        })
    }, [])

    const updateStatus = (status) => {
        if (!template_id || submitting) return
        setSubmitting(status)
        setError('')
        dispatch(
            TemplateService.approveTemplate({
                templateID: template_id,
                status,
            })
        ).then((resp) => {
            if (resp.error) {
                setSubmitting(null)
                setError(
                    `The template status was not changed: ${errorMessage({
                        data: resp.payload,
                    })}`
                )
                return
            }
            setTimeout(() => {
                navigate('/templates')
            }, 4000)
        })
    }

    const spinnerFor = (status) =>
        submitting === status ? (
            <CircularProgress size={16} sx={{ color: 'inherit' }} />
        ) : null

    return (
        <Container title="Approve Template">
            <Grid container display="flex" flexDirection="column">
                {/* Until the body arrives the white preview box would sit
                    empty with no sign that anything is coming. */}
                {load.state === 'loading' && (
                    <Grid item>
                        <InlineLoading label="Loading the template…" py={6} />
                    </Grid>
                )}
                <Grid
                    item
                    sx={{
                        display: load.state === 'loading' ? 'none' : undefined,
                    }}
                >
                    <Box
                        sx={{
                            border: `1px solid ${SURFACE.border}`,
                            borderRadius: `${RADIUS.md}px`,
                            overflow: 'hidden',
                            // The template renders on an email client's
                            // white in both themes.
                            background: SURFACE.emailCanvas,
                        }}
                    >
                        <TemplateRenderer />
                    </Box>
                </Grid>
                {error && (
                    <Grid item sx={{ mt: 2 }}>
                        <ErrorBanner message={error} />
                    </Grid>
                )}
                {load.state === 'missing' && (
                    <Grid item sx={{ mt: 2 }}>
                        <ErrorBanner
                            message={`This template could not be loaded, so it cannot be approved: ${load.reason}`}
                        />
                    </Grid>
                )}
                {load.state === 'ready' && (
                    <Grid
                        item
                        display="flex"
                        flexDirection="row"
                        justifyContent="center"
                    >
                        <Button
                            variant="contained"
                            color="success"
                            onClick={() => updateStatus('approved')}
                            disabled={Boolean(submitting)}
                            startIcon={spinnerFor('approved')}
                            sx={decisionButtonSx}
                        >
                            Approve
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={() => updateStatus('disapproved')}
                            disabled={Boolean(submitting)}
                            startIcon={spinnerFor('disapproved')}
                            sx={decisionButtonSx}
                        >
                            Disapprove
                        </Button>
                    </Grid>
                )}
            </Grid>
        </Container>
    )
}

export default ApproveTemplate
